/**
 * «Hola Donna»: la palabra de activación y las órdenes por voz. Las órdenes solo navegan, buscan o
 * abren el dictado: nunca firman, guardan, cobran ni borran (eso siempre se confirma en pantalla).
 */
import { norm } from "./texto.ts";

export type Pestana = "consulta" | "ficha" | "odontograma" | "evolucion" | "especialidades" | "plan" | "fotos" | "documentos";

export type Orden =
  | { tipo: "ir"; destino: "inicio" | "agenda" | "mensajes" | "cumpleanos" | "configuracion" | "respaldos" | "reportes" | "facturas" | "nuevo" }
  | { tipo: "pestana"; pestana: Pestana }
  | { tipo: "buscar"; texto: string; pestana?: Pestana }
  | { tipo: "elegir"; n: number }
  | { tipo: "dictar" }
  | { tipo: "detener_dictado" }
  | { tipo: "historia_pdf" }
  | { tipo: "ayuda" }
  | { tipo: "resumen_dia" }
  | { tipo: "charla"; clase: "gracias" | "estado" | "despedida" | "saludo" }
  | { tipo: "no_permitida"; accion: string }
  | { tipo: "desconocida"; texto: string };

/** Cómo suele escribir el reconocedor de voz «Hola Donna». */
const ACTIVACION = /^(?:hola|ola|oye|oiga|hey|ok|okey|okay|buenos dias|buenas tardes|buenas noches)?[\s,.!¡]*(?:donna|dona|donha|donas)\b[\s,.!:]*/;

/**
 * Si el texto empieza con la palabra de activación, devuelve lo que viene después (la orden, que puede
 * estar vacía si la persona hace una pausa); si no, null.
 */
export function trasActivacion(texto: string): string | null {
  const t = norm(texto).replace(/[¿?]/g, "").trim();
  const m = t.match(ACTIVACION);
  if (!m) return null;
  // «dona» a secas es una palabra común: solo cuenta si va con saludo o al inicio de una orden reconocible
  const saludo = /^(hola|ola|oye|oiga|hey|ok|okey|okay|buen)/.test(t);
  const resto = t.slice(m[0].length).trim();
  if (!saludo && !/^donna\b/.test(t) && interpretarOrden(resto).tipo === "desconocida") return null;
  return resto;
}

const PESTANAS: readonly [Pestana, RegExp][] = [
  ["odontograma", /odontograma/],
  ["evolucion", /evolucion|notas? de evolucion/],
  ["plan", /plan( de tratamiento)?|presupuesto|pagos|abonos|saldo|cuenta/],
  ["fotos", /fotos?|fotografias?|imagenes/],
  ["documentos", /recetas?|consentimientos?|certificados?|documentos/],
  ["especialidades", /endodoncia|ortodoncia|implantes?|implantologia|especialidad(es)?/],
  ["ficha", /ficha|historia( clinica)?|filiacion|antecedentes|anamnesis/],
  ["consulta", /consulta/],
];

const NO_PERMITIDAS: readonly [RegExp, string][] = [
  [/\b(firma|firmar|firmala|firmalo)\b/, "firmar"],
  [/\b(guarda|guardar|guardalo|guardala|aplica|aplicar)\b/, "guardar"],
  [/\b(cobra|cobrar|registra (el |un )?(abono|pago)|anula|anular)\b/, "cobrar o anular"],
  [/\b(borra|borrar|elimina|eliminar|quita|quitar)\b/, "borrar"],
  [/\b(envia|enviar|manda|mandar)\b/, "enviar mensajes"],
  [/\b(facturar|facturale|facturalo|facturala|emite|emitir)\b/, "emitir facturas"],
];

export function interpretarOrden(texto: string): Orden {
  const t = norm(texto).replace(/[¿?¡!.,;:]/g, " ").replace(/\s+/g, " ").trim()
    .replace(/^(por favor|porfa|porfavor)\s+/, "").replace(/\s+(por favor|porfa)$/, "");
  if (!t) return { tipo: "desconocida", texto: "" };
  for (const [re, accion] of NO_PERMITIDAS) if (re.test(t)) return { tipo: "no_permitida", accion };
  if (/^(ayuda|que puedes hacer|que sabes hacer|que ordenes|comandos)/.test(t)) return { tipo: "ayuda" };
  // el resumen de la jornada y un poco de conversación (no hacen nada en el sistema)
  if (/^(resumen( del dia| de hoy)?|dame (el )?resumen( del dia)?|como (esta|va|sera) (mi|el) dia|que (tengo|hay) (para )?hoy|cuentame (mi dia|como va el dia)|como estamos hoy|mi dia)$/.test(t)) return { tipo: "resumen_dia" };
  if (/^(muchas |mil )?gracias( donna| por todo| por tu ayuda)?$/.test(t)) return { tipo: "charla", clase: "gracias" };
  if (/^(como estas|como te va|como andas|como amaneciste|que tal)( hoy| donna)?$/.test(t)) return { tipo: "charla", clase: "estado" };
  if (/^(adios|hasta luego|hasta manana|chao|chau|nos vemos|que descanses)( donna)?$/.test(t)) return { tipo: "charla", clase: "despedida" };
  if (/^(hola|buenos dias|buenas tardes|buenas noches|buenas)( donna)?$/.test(t)) return { tipo: "charla", clase: "saludo" };
  if (/^(deja de|para|detente|termina|deten|stop)( de)? (dictar|el dictado|de escuchar)|^(para|deten|termina) (el )?dictado/.test(t)) return { tipo: "detener_dictado" };
  if (/^(dicta|dictar|empieza a dictar|empecemos|comienza a dictar|inicia (el )?dictado|vamos a dictar|voy a dictar|escribe lo que (digo|te diga)|toma nota)/.test(t)) return { tipo: "dictar" };
  if (/(historia|expediente).*(pdf|imprim)|(pdf|imprim).*(historia|expediente)|exporta(r)? (la )?historia/.test(t)) return { tipo: "historia_pdf" };

  // «abre el segundo», «el número 3», «la primera»: elegir de la lista que Donna acaba de leer
  const el = t.match(/^(?:(?:abre|abrir|elige|escoge|selecciona|quiero)\s+)?(?:a\s+)?(?:el|la|al)?\s*(?:numero|opcion|paciente)?\s*(primer[oa]?|segund[oa]|tercer[oa]?|cuart[oa]|quint[oa]|uno|una|dos|tres|cuatro|cinco|[1-9])$/);
  if (el) return { tipo: "elegir", n: ORDINAL[el[1]] ?? Number(el[1]) };

  const verbo = /^(abre|abrir|abreme|ve a|ir a|vamos a|muestra(me)?|ensename|lleva(me)? a|entra (a|en))\s+(el |la |los |las |mis |mi )?/;
  const destino = t.replace(verbo, "").replace(/^(el|la|los|las|mis|mi) /, "").replace(/^(pestana|seccion|pantalla) (de |del )?/, "").trim();
  const conVerbo = verbo.test(t) || destino === t.replace(/^(el|la|los|las|mis|mi) /, "");
  if (conVerbo) {
    if (/^(agenda|citas( de hoy)?|calendario)$/.test(destino)) return { tipo: "ir", destino: "agenda" };
    if (/^(inicio|principal|pantalla principal|pacientes recientes)$/.test(destino)) return { tipo: "ir", destino: "inicio" };
    if (/^(mensajes|recordatorios|whatsapp)$/.test(destino)) return { tipo: "ir", destino: "mensajes" };
    if (/^(cumpleanos|cumpleaneros|cumpleanos del mes|cumpleanos de (hoy|la semana))$/.test(destino)) return { tipo: "ir", destino: "cumpleanos" };
    if (/^(configuracion|ajustes|engranaje)$/.test(destino)) return { tipo: "ir", destino: "configuracion" };
    if (/^(respaldos?|copias de seguridad|backup)$/.test(destino)) return { tipo: "ir", destino: "respaldos" };
    if (/^(reportes?|informes?|estadisticas|ingresos( del mes)?|caja del mes)$/.test(destino)) return { tipo: "ir", destino: "reportes" };
    if (/^(facturas?( electronicas)?|facturacion|comprobantes( electronicos)?)$/.test(destino)) return { tipo: "ir", destino: "facturas" };
    if (/^(nuevo paciente|registr(a|ar) (un )?(nuevo )?paciente|paciente nuevo)$/.test(destino)) return { tipo: "ir", destino: "nuevo" };
    for (const [p, re] of PESTANAS) {
      if (new RegExp(`^(${re.source})$`).test(destino)) return { tipo: "pestana", pestana: p };
      // «abre el odontograma de Lucía Paredes»: busca a la paciente y abre esa pestaña
      const m = destino.match(new RegExp(`^(?:${re.source}) (?:de|del|a|para) (?:la |el )?(?:paciente )?(.{2,60})$`));
      if (m) return { tipo: "buscar", texto: m[m.length - 1].trim(), pestana: p };
    }
  }
  if (/^(nuevo paciente|registra(r)? (un )?(nuevo )?paciente|paciente nuevo|crea(r)? (un )?paciente)$/.test(t)) return { tipo: "ir", destino: "nuevo" };
  // «busca a Lucía Paredes», «busca y abre a…», «abre al paciente…», «paciente Lucía Paredes», «abre a Pedro»
  const b = t.match(/^(?:(?:busca(?:r|me)?|encuentra|localiza)(?: y abre)?|abre|abrir|abreme|muestrame|ve a|ir a|paciente)\s+(?:a(?:l)?\s+|la\s+|el\s+)?(?:paciente\s+|historia(?: clinica)?(?: numero| n)?\s+|hc\s+|ficha(?: de| del)?\s+)?(.{2,60})$/);
  if (b && !/^(el |la )?(odontograma|agenda|plan|historia|ficha|consulta)$/.test(b[1])) return { tipo: "buscar", texto: b[1].replace(/^(de|del)\s+/, "").trim() };
  return { tipo: "desconocida", texto: t };
}

const ORDINAL: Record<string, number> = {
  primer: 1, primero: 1, primera: 1, uno: 1, una: 1, segundo: 2, segunda: 2, dos: 2, tercer: 3, tercero: 3, tercera: 3, tres: 3,
  cuarto: 4, cuarta: 4, cuatro: 4, quinto: 5, quinta: 5, cinco: 5,
};

/**
 * Entre los resultados de la búsqueda, el paciente que corresponde a lo dicho: si hay uno solo, ese; si hay
 * varios, el único cuyo nombre completo coincide exactamente (en cualquier orden) o, con número, su historia.
 */
export function pacienteDicho<T extends { nombres: string; apellidos: string; hc?: number; doc?: string }>(dicho: string, lista: T[]): T | null {
  if (lista.length === 1) return lista[0];
  const d = norm(dicho).replace(/\s+/g, " ").trim();
  const exactos = lista.filter((p) => {
    const n = norm(p.nombres).trim(), a = norm(p.apellidos).trim();
    return [`${n} ${a}`, `${a} ${n}`, n, a].includes(d) && (d.includes(" ") || [`${n} ${a}`, `${a} ${n}`].includes(d)) || String(p.hc ?? "") === d || (p.doc && p.doc === d.replace(/\D/g, ""));
  });
  return exactos.length === 1 ? exactos[0] : null;
}

/** Lo que Donna responde (en pantalla) a cada orden. */
export function respuestaOrden(o: Orden): string {
  switch (o.tipo) {
    case "ir": return { inicio: "Vamos al inicio.", agenda: "Abro la agenda.", mensajes: "Abro los mensajes.", cumpleanos: "Abro los cumpleaños.", configuracion: "Abro la configuración.", respaldos: "Abro los respaldos.", reportes: "Abro los reportes.", facturas: "Abro las facturas.", nuevo: "Registremos un paciente nuevo." }[o.destino];
    case "pestana": return `Abro ${({ consulta: "la consulta", ficha: "la ficha", odontograma: "el odontograma", evolucion: "la evolución", especialidades: "las especialidades", plan: "el plan y los pagos", fotos: "las fotos", documentos: "recetas y consentimientos" } as const)[o.pestana]}.`;
    case "buscar": return `Busco a «${o.texto}».`;
    case "elegir": return `Abro el número ${o.n}.`;
    case "dictar": return "Te escucho: dicta la consulta.";
    case "detener_dictado": return "Dejé de dictar.";
    case "historia_pdf": return "Preparo la historia clínica completa para imprimir o guardar en PDF.";
    case "ayuda": return AYUDA_ORDENES;
    case "resumen_dia": return "Te cuento cómo va tu día.";
    case "charla": return { gracias: "Con gusto.", estado: "Muy bien, gracias. ¿En qué te ayudo?", despedida: "Hasta luego.", saludo: "Hola, te escucho." }[o.clase];
    case "no_permitida": return `Por seguridad no puedo ${o.accion} por voz: hazlo tú en la pantalla.`;
    case "desconocida": return o.texto ? `No entendí «${o.texto}». Di «Hola Donna, ayuda» para ver lo que puedo hacer.` : "Te escucho…";
  }
}

export const AYUDA_ORDENES = "Puedes decir: «abre a Lucía Paredes» (busca y abre su ficha), «abre el odontograma de Lucía Paredes», «abre la historia número 45», «abre el segundo» (cuando hay varios), «abre la agenda», «nuevo paciente», «abre el plan», «dicta» o «deja de dictar», «imprime la historia», «abre los reportes», «abre las facturas», «resumen» (cómo va tu día).";
